// components/AdminMessaging/ProactiveMessage.tsx
// Component for sending proactive (admin-initiated) messages to users

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    sendProactiveMessage,
    searchUsers,
    validateResponse,
    getFilePreviewUrl,
    revokePreviewUrl,
} from '../../services/adminMessaging';
import './AdminMessaging.css';

type ResponseType = 'text' | 'voice' | 'image' | 'mixed';

export const ProactiveMessage: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    // User selection state
    const [toAppleID, setToAppleID] = useState('');
    const [toProfileID, setToProfileID] = useState('');
    const [toExplorerName, setToExplorerName] = useState('');

    // User search/autocomplete state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{
        appleID: string;
        profileID: string;
        explorerName: string;
        lastMessageDate?: Date;
    }>>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // Response composer state
    const [responseType, setResponseType] = useState<ResponseType>('text');
    const [textContent, setTextContent] = useState('');
    const [voiceFile, setVoiceFile] = useState<File | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageCaption, setImageCaption] = useState('');
    const [voicePreviewUrl, setVoicePreviewUrl] = useState<string>('');
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');

    useEffect(() => {
        // Cleanup preview URLs on unmount
        return () => {
            if (voicePreviewUrl) revokePreviewUrl(voicePreviewUrl);
            if (imagePreviewUrl) revokePreviewUrl(imagePreviewUrl);
        };
    }, [voicePreviewUrl, imagePreviewUrl]);

    // Load initial users on mount
    useEffect(() => {
        const loadInitialUsers = async () => {
            setIsSearching(true);
            try {
                const results = await searchUsers('');
                setSearchResults(results.slice(0, 20)); // Show 20 most recent users
                console.log('Loaded users:', results.length); // Debug log
            } catch (err) {
                console.error('Failed to load users:', err);
            } finally {
                setIsSearching(false);
            }
        };
        loadInitialUsers();
    }, []);

    // Handle user search with debouncing
    useEffect(() => {
        const searchTimeout = setTimeout(async () => {
            if (searchQuery.trim().length >= 1) {
                setIsSearching(true);
                try {
                    const results = await searchUsers(searchQuery);
                    setSearchResults(results);
                    setShowDropdown(true);
                    console.log('Search results:', results.length); // Debug log
                } catch (err) {
                    console.error('Search failed:', err);
                    setSearchResults([]);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setShowDropdown(false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(searchTimeout);
    }, [searchQuery]);

    const handleUserSelect = (user: {
        appleID: string;
        profileID: string;
        explorerName: string;
    }) => {
        setToAppleID(user.appleID);
        setToProfileID(user.profileID);
        setToExplorerName(user.explorerName);
        setSearchQuery(user.explorerName);
        setShowDropdown(false);
    };

    const handleSearchFocus = () => {
        if (searchResults.length > 0) {
            setShowDropdown(true);
        }
    };

    const handleVoiceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setVoiceFile(file);
            if (voicePreviewUrl) revokePreviewUrl(voicePreviewUrl);
            setVoicePreviewUrl(getFilePreviewUrl(file));
        }
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            if (imagePreviewUrl) revokePreviewUrl(imagePreviewUrl);
            setImagePreviewUrl(getFilePreviewUrl(file));
        }
    };

    const handleSendMessage = async () => {
        // Validate user details
        if (!toAppleID.trim() || !toProfileID.trim() || !toExplorerName.trim()) {
            alert('Please fill in all user details (Apple ID, Profile ID, and Explorer Name)');
            return;
        }

        // Validate message content
        const validation = validateResponse({
            responseType,
            textContent,
            voiceFile: voiceFile || undefined,
            imageFile: imageFile || undefined,
        });

        if (!validation.isValid) {
            alert(`Invalid message:\n\n${validation.errors.join('\n')}`);
            return;
        }

        if (!currentUser?.email) {
            alert('User not authenticated');
            return;
        }

        try {
            setSending(true);
            setError('');

            await sendProactiveMessage({
                toAppleID: toAppleID.trim(),
                toProfileID: toProfileID.trim(),
                toExplorerName: toExplorerName.trim(),
                responseType,
                textContent: textContent || undefined,
                voiceFile: voiceFile || undefined,
                imageFile: imageFile || undefined,
                imageCaption: imageCaption || undefined,
                sentByAdminEmail: currentUser.email,
            });

            alert('✅ Message sent successfully!');
            navigate('/admin-messaging');
        } catch (err: any) {
            console.error('Failed to send message:', err);
            setError(err.message || 'Failed to send message');
            alert(`❌ Failed to send message:\n\n${err.message}`);
        } finally {
            setSending(false);
        }
    };

    const isValidMessage = (): boolean => {
        if (!toAppleID.trim() || !toProfileID.trim() || !toExplorerName.trim()) {
            return false;
        }

        const validation = validateResponse({
            responseType,
            textContent,
            voiceFile: voiceFile || undefined,
            imageFile: imageFile || undefined,
        });
        return validation.isValid;
    };

    return (
        <div className="messaging-container">
            <header className="messaging-header">
                <div className="header-content">
                    <button
                        onClick={() => navigate('/admin-messaging')}
                        className="back-button"
                    >
                        ← Back to Inbox
                    </button>
                    <div>
                        <h1>📤 Send New Message</h1>
                        <p className="header-subtitle">
                            Send a proactive message to a user
                        </p>
                    </div>
                </div>
            </header>

            <main className="message-detail-main">
                {error && (
                    <div className="error-banner">
                        <span>⚠️ {error}</span>
                    </div>
                )}

                {/* User Selection */}
                <section className="message-info-section">
                    <div className="info-header">
                        <h2>Select Recipient</h2>
                        <p className="section-description">
                            Search and select any registered user
                        </p>
                    </div>

                    {/* User Search */}
                    <div className="user-search-section">
                        <div className="form-field">
                            <label>👤 Explorer Name: *</label>
                            <div className="search-input-container">
                                <input
                                    type="text"
                                    className="form-input search-input"
                                    placeholder="Type explorer name to search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={handleSearchFocus}
                                    autoComplete="off"
                                />
                                {isSearching && (
                                    <span className="search-spinner">⏳</span>
                                )}
                            </div>
                            <span className="field-hint">
                                Type at least 1 character to search. {searchResults.length > 0 && `Found ${searchResults.length} user(s).`}
                            </span>

                            {/* Autocomplete Dropdown */}
                            {showDropdown && searchResults.length > 0 && (
                                <div className="autocomplete-dropdown">
                                    {searchResults.map((user, index) => (
                                        <div
                                            key={`${user.appleID}_${user.profileID}_${index}`}
                                            className="autocomplete-item"
                                            onClick={() => handleUserSelect(user)}
                                        >
                                            <div className="user-name">
                                                👤 {user.explorerName}
                                            </div>
                                            <div className="user-ids">
                                                <span>Apple ID: {user.appleID}</span>
                                                <span className="separator">•</span>
                                                <span>Profile: {user.profileID.substring(0, 12)}...</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {showDropdown && searchQuery.length >= 1 && searchResults.length === 0 && !isSearching && (
                                <div className="autocomplete-dropdown">
                                    <div className="no-results">
                                        No users found matching "{searchQuery}"
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Show selected user details (compact banner) */}
                    {toAppleID && toProfileID && (
                        <div className="selected-user-banner-compact">
                            <div className="selected-user-info-compact">
                                <span className="selected-icon">✅</span>
                                <div className="selected-user-text">
                                    <strong>{toExplorerName}</strong>
                                    <span className="user-ids-compact">
                                        Apple ID: {toAppleID} • Profile: {toProfileID.substring(0, 12)}...
                                    </span>
                                </div>
                            </div>
                            <button
                                className="change-user-button-compact"
                                onClick={() => {
                                    setToAppleID('');
                                    setToProfileID('');
                                    setToExplorerName('');
                                    setSearchQuery('');
                                }}
                            >
                                🔄 Change
                            </button>
                        </div>
                    )}
                </section>

                {/* Message Composer */}
                <section className="response-composer-section">
                    <h2>Compose Message</h2>

                    {/* Response Type Selector */}
                    <div className="response-type-selector">
                        <label>Message Type:</label>
                        <div className="type-buttons">
                            <button
                                className={`type-button ${responseType === 'text' ? 'active' : ''}`}
                                onClick={() => setResponseType('text')}
                            >
                                💬 Text
                            </button>
                            <button
                                className={`type-button ${responseType === 'voice' ? 'active' : ''}`}
                                onClick={() => setResponseType('voice')}
                            >
                                🎙️ Voice
                            </button>
                            <button
                                className={`type-button ${responseType === 'image' ? 'active' : ''}`}
                                onClick={() => setResponseType('image')}
                            >
                                🖼️ Image
                            </button>
                            <button
                                className={`type-button ${responseType === 'mixed' ? 'active' : ''}`}
                                onClick={() => setResponseType('mixed')}
                            >
                                🎨 Mixed
                            </button>
                        </div>
                    </div>

                    {/* Text Content */}
                    {(responseType === 'text' || responseType === 'mixed') && (
                        <div className="response-field">
                            <label>Text Message:</label>
                            <textarea
                                className="text-input"
                                placeholder="Type your message..."
                                value={textContent}
                                onChange={(e) => setTextContent(e.target.value)}
                                rows={6}
                                maxLength={5000}
                            />
                            <span className="char-count">
                                {textContent.length} / 5000 characters
                            </span>
                        </div>
                    )}

                    {/* Voice Upload */}
                    {(responseType === 'voice' || responseType === 'mixed') && (
                        <div className="response-field">
                            <label>Voice Message:</label>
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={handleVoiceFileChange}
                                className="file-input"
                            />
                            {voiceFile && (
                                <div className="file-preview">
                                    <span>🎙️ {voiceFile.name}</span>
                                    <span className="file-size">
                                        {(voiceFile.size / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                    {voicePreviewUrl && (
                                        <audio controls src={voicePreviewUrl} />
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Image Upload */}
                    {(responseType === 'image' || responseType === 'mixed') && (
                        <div className="response-field">
                            <label>Image:</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageFileChange}
                                className="file-input"
                            />
                            {imageFile && (
                                <div className="file-preview">
                                    <span>🖼️ {imageFile.name}</span>
                                    <span className="file-size">
                                        {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                    {imagePreviewUrl && (
                                        <img
                                            src={imagePreviewUrl}
                                            alt="Preview"
                                            className="image-preview"
                                        />
                                    )}
                                </div>
                            )}

                            <label className="caption-label">Image Caption (optional):</label>
                            <input
                                type="text"
                                className="caption-input"
                                placeholder="Add a caption for your image..."
                                value={imageCaption}
                                onChange={(e) => setImageCaption(e.target.value)}
                                maxLength={500}
                            />
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="response-actions">
                        <button
                            className="send-button"
                            onClick={handleSendMessage}
                            disabled={sending || !isValidMessage()}
                        >
                            {sending ? (
                                <>
                                    <span className="spinner-small"></span>
                                    Sending...
                                </>
                            ) : (
                                <>📤 Send Message</>
                            )}
                        </button>
                        <button
                            className="cancel-button"
                            onClick={() => navigate('/admin-messaging')}
                            disabled={sending}
                        >
                            Cancel
                        </button>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default ProactiveMessage;
